import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, KeyRound, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function SecuritySettingsPage() {
  const navigate = useNavigate();
  const role = (localStorage.getItem('userRole') || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin';

  const [status, setStatus] = useState(null);          // {enabled, recovery_codes_remaining}
  const [policy, setPolicy] = useState(null);          // {require_staff_2fa}
  const [setup, setSetup] = useState(null);            // {qr, manual_secret}
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [recovery, setRecovery] = useState(null);      // string[]
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const s = await axios.get(`${API}/auth/2fa/status`, authHeader());
      setStatus(s.data);
    } catch { toast.error('Status konnte nicht geladen werden'); }
    if (isAdmin) {
      try {
        const p = await axios.get(`${API}/auth/2fa/policy`, authHeader());
        setPolicy(p.data);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`, {}, authHeader());
      setSetup(r.data); setRecovery(null); setCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Einrichtung fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/activate`, { code: code.trim() }, authHeader());
      setRecovery(r.data.recovery_codes || []);
      setSetup(null);
      toast.success('2FA aktiviert');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code ungültig');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode.trim() }, authHeader());
      setDisableCode(''); setRecovery(null);
      toast.success('2FA deaktiviert');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code ungültig');
    } finally { setBusy(false); }
  };

  const togglePolicy = async (v) => {
    try {
      const r = await axios.put(`${API}/auth/2fa/policy`, { require_staff_2fa: v }, authHeader());
      setPolicy(r.data);
      toast.success(v ? '2FA-Pflicht aktiviert' : '2FA-Pflicht deaktiviert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText((recovery || []).join('\n'));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6" data-testid="security-settings-page">
      <button onClick={() => navigate('/settings')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="security-back">
        <ArrowLeft className="h-4 w-4" /> Zurück zu Einstellungen
      </button>

      {/* Persönliche 2FA */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Zwei-Faktor-Authentifizierung</CardTitle>
          <CardDescription>Schütze dein Konto mit einem zusätzlichen Code aus einer Authenticator-App (z. B. Google Authenticator).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status && (
            <div className="flex items-center gap-2 text-sm" data-testid="twofa-status">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${status.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
              {status.enabled
                ? <span>Aktiv · {status.recovery_codes_remaining} Recovery-Codes übrig</span>
                : <span>Nicht aktiv</span>}
            </div>
          )}

          {/* Recovery-Codes nach Aktivierung */}
          {recovery && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="recovery-codes">
              <div className="flex items-center justify-between">
                <Label className="text-amber-900 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Recovery-Codes (einmalig speichern!)</Label>
                <Button size="sm" variant="outline" onClick={copyRecovery} data-testid="copy-recovery">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-1 font-mono text-sm text-amber-900">
                {recovery.map((c) => <div key={c}>{c}</div>)}
              </div>
              <p className="text-xs text-amber-800">Bewahre diese Codes sicher auf – jeder funktioniert einmal, falls du keinen Zugriff auf die App hast.</p>
            </div>
          )}

          {/* Einrichtung */}
          {!status?.enabled && !setup && (
            <Button onClick={startSetup} disabled={busy} data-testid="twofa-setup-btn">2FA einrichten</Button>
          )}

          {setup && (
            <div className="space-y-3" data-testid="twofa-setup-panel">
              <p className="text-sm text-gray-600">Scanne den QR-Code mit deiner Authenticator-App oder gib den Schlüssel manuell ein:</p>
              {setup.qr && <img src={setup.qr} alt="2FA QR" className="w-44 h-44 border rounded-lg bg-white" data-testid="twofa-qr" />}
              <div className="text-xs font-mono bg-gray-100 rounded p-2 break-all">{setup.manual_secret}</div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="twofa-code">6-stelliger Code</Label>
                  <Input id="twofa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" data-testid="twofa-code-input" />
                </div>
                <Button onClick={activate} disabled={busy || code.trim().length < 6} data-testid="twofa-activate-btn">Aktivieren</Button>
              </div>
            </div>
          )}

          {/* Deaktivieren */}
          {status?.enabled && !recovery && (
            <div className="flex items-end gap-2 pt-2 border-t">
              <div className="flex-1">
                <Label htmlFor="twofa-disable">2FA deaktivieren (aktuellen Code eingeben)</Label>
                <Input id="twofa-disable" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 oder Recovery-Code" data-testid="twofa-disable-input" />
              </div>
              <Button variant="destructive" onClick={disable} disabled={busy || !disableCode.trim()} data-testid="twofa-disable-btn">Deaktivieren</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2FA-Pflicht (nur Admin) */}
      {isAdmin && policy && (
        <Card>
          <CardHeader>
            <CardTitle>2FA-Pflicht für Mitarbeitende</CardTitle>
            <CardDescription>Ist diese Option aktiv, müssen alle Mitarbeitenden beim nächsten Login die Zwei-Faktor-Authentifizierung einrichten.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Switch checked={!!policy.require_staff_2fa} onCheckedChange={togglePolicy} data-testid="twofa-policy-switch" />
              <Label>2FA für alle Mitarbeitenden verpflichtend</Label>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
